import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import MemberCard from '../../components/MemberCard';
import PageTitle from '../../components/PageTitle';
import { getEffectiveRoleForSorting, getRankScore } from '../../utils/rank';
import api from '../../services/api';

interface Member {
  id: string;
  fullName: string;
  photo: string;
  memberType: string;
  rank: string;
  memberStatus: string;
  designation: string;
  branch: string;
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Youth = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        // Fetch all active members and filter for Youth types
        const response = await api.getPublicMembers(undefined, 'Active');

        const membersData = (response.members || [])
          .map((data: any) => ({
            id: data.id || data.user_id,
            fullName: data.full_name || 'Unknown',
            photo: data.photo || '/default-avatar.png',
            memberType: data.member_type || 'Member',
            rank: data.rank || 'Member',
            memberStatus: data.member_status || 'Active',
            designation: data.designation || 'Member',
            branch: data.branch || ''
          }))
          .filter((m: Member) => (m.memberType || '').trim().startsWith('Youth '))
          .sort((a: Member, b: Member) => {
            const aScore = getRankScore(getEffectiveRoleForSorting(a.rank, a.designation));
            const bScore = getRankScore(getEffectiveRoleForSorting(b.rank, b.designation));
            if (aScore !== bScore) return aScore - bScore;
            return a.fullName.localeCompare(b.fullName);
          });

        setMembers(membersData);
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, []);

  return (
    <div className="bg-gray-900 min-h-screen">
      <PageTitle
        title="Youth Members"
        subtitle="View all youth members"
      />

      <div className="container mx-auto px-4 py-8 sm:py-12">
        {loading ? (
          <div className="flex justify-center items-center min-h-[300px] sm:min-h-[400px]">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : members.length === 0 ? (
          <div className="text-center text-gray-400">
            No members found
          </div>
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 max-w-7xl mx-auto"
          >
            {members.map((member) => (
              <MemberCard
                key={member.id}
                name={member.fullName}
                photoUrl={member.photo}
                rank={member.rank}
                designation={member.designation}
                memberType={member.memberType}
                branch={member.branch}
              />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Youth;
